import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Text, Divider, Switch,
  FormControl, FormLabel, Input, IconButton, useToast, Box,
  Alert, AlertIcon, AlertTitle, AlertDescription,
} from "@chakra-ui/react";
import PatientPhones from "./sms/PatientPhones";
import { rowPhones } from "../utils/smsPhone";
import { getBackendURL } from "./env";
import {
  fetchConfirmationSchedule,
  confirmationWindowWarning,
  smsOffWarning,
  reconcileConfirmationReplies,
  buildReconcileView,
} from "../utils/confirmationScheduling";
import ProcessConfirmationsDialog from "./reminders/ProcessConfirmationsDialog";

// One definition of "unconfirmed", shared by the modal and the Admin page.
// C and R are both answered — R is terminal for automation, so a patient who
// asked to reschedule is never re-texted until staff resets them to U.
export const isConfirmationOpen = (patient) => {
  const status = String(patient?.confirmation_status || "").toUpperCase();
  return status !== "C" && status !== "R";
};

// The schedule is authored in days but stored in hours, because the sweep
// computes `appointment - hours_before`. Half days are allowed so a sub-day
// reminder is still expressible.
const hoursToDays = (hours) => {
  const n = parseFloat(hours);
  return Number.isFinite(n) ? Math.round((n / 24) * 100) / 100 : "";
};

const daysToHours = (days) => {
  const n = parseFloat(days);
  return Number.isFinite(n) ? Math.max(1, Math.round(n * 24)) : 24;
};

// Any number the patient can be texted on — cell or home (utils/smsPhone).
const hasPhone = (patient) => rowPhones(patient).length > 0;
const hasEmail = (patient) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(String(patient?.email || "").trim());

export const ruleLabel = (row) => {
  const rule = String(row?.confirmation_rule || "");
  if (rule === "manual") return "Manual";
  if (rule === "welcome") return "Welcome message";
  if (rule === "email") return "Email";
  const days = hoursToDays(row?.hours_before);
  if (days === "" || days === undefined) return rule || "Scheduled";
  return `${days} ${days === 1 ? "day" : "days"} before`;
};

const AppointmentConfirmationModal = ({
  isOpen,
  onClose,
  schema,
  patients = [],
  onSelectAllUnconfirmed,
  date,
  // Who is applying replies (stamped as confirmation_updated_by), and what to
  // call after statuses changed so the table reloads.
  userEmail,
  onStatusesChanged,
  // Every provider schema the page is showing (its rows fan in from each).
  // Apply replies checks all of them, not just the home `schema`.
  providerSchemas = [],
}) => {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  // The backend's verdict on whether any text can leave this schema (the
  // SMS kill switch). null until the settings load; only an explicit false
  // shows the warning.
  const [messagingEnabled, setMessagingEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  // { rows, autoStatus, windowDays } — only autoStatus/windowDays are used
  // here now: the per-date queue moved to the Send Reminders modal's
  // Confirmation reminders tab.
  const [schedule, setSchedule] = useState(null);

  const tally = useMemo(() => {
    const out = { confirmed: 0, reschedule: 0, awaiting: 0 };
    patients.forEach((p) => {
      const status = String(p?.confirmation_status || "").toUpperCase();
      if (status === "C") out.confirmed += 1;
      else if (status === "R") out.reschedule += 1;
      else out.awaiting += 1;
    });
    return out;
  }, [patients]);

  const rescheduleRequests = useMemo(
    () =>
      patients.filter(
        (p) => String(p?.confirmation_status || "").toUpperCase() === "R",
      ),
    [patients],
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${getBackendURL()}/twilio/confirmation-settings/?schema=${encodeURIComponent(schema || "public")}`,
        );
        const payload = await response.json();
        if (!cancelled && typeof payload?.messaging_enabled === "boolean") {
          setMessagingEnabled(payload.messaging_enabled);
        }
        if (!cancelled && payload?.settings) {
          // The API speaks hours; the UI speaks days. Convert on the way in
          // and back again on save, so `days_before` is only ever a display
          // value and `hours_before` stays the stored contract.
          setSettings({
            ...payload.settings,
            rules: (payload.settings.rules || []).map((r) => ({
              ...r,
              days_before: hoursToDays(r.hours_before),
            })),
          });
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: "Could not load confirmation schedule",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, schema, toast]);

  // `patients` can be fanned in from every selected provider schema (the
  // Admin patient list spans the whole clinic group), so the automatic-
  // confirmation schedule has to be pulled per schema too — a single fetch
  // scoped to `schema` (the viewer's home schema) left every OTHER schema's
  // "won't receive a scheduled confirmation" patients silently invisible.
  // Falls back to `schema` alone when nothing is `_schema`-tagged, which
  // keeps single-schema callers identical to the old behavior.
  const scheduleSchemas = useMemo(() => {
    const tagged = [...new Set(patients.map((p) => p?._schema).filter(Boolean))];
    return tagged.length ? tagged : [schema || "public"];
  }, [patients, schema]);

  const loadSeq = React.useRef(0);
  const loadSchedule = React.useCallback(async () => {
    if (!date) return;
    const seq = ++loadSeq.current;
    const results = await Promise.all(
      scheduleSchemas.map((s) => fetchConfirmationSchedule({ schema: s, date })),
    );
    // Overlapping loads (Save then a quick Cancel, or a date/schema change
    // while open) can resolve out of order. Only the most recently issued
    // request is allowed to apply its result.
    if (seq !== loadSeq.current) return;
    // Namespace by schema so ids that collide across schemas never shadow
    // each other's auto-confirmation status.
    const autoStatus = {};
    let windowDays = null;
    results.forEach((result, i) => {
      const s = scheduleSchemas[i];
      const map = result?.autoStatus || {};
      Object.keys(map).forEach((id) => {
        autoStatus[`${s}::${id}`] = map[id];
      });
      if (windowDays === null && result?.windowDays != null) {
        windowDays = result.windowDays;
      }
    });
    setSchedule({ autoStatus, windowDays });
  }, [scheduleSchemas, date]);

  useEffect(() => {
    if (!isOpen) return;
    loadSchedule();
  }, [isOpen, loadSchedule]);

  const noAutoPatients = useMemo(() => {
    const map = schedule?.autoStatus || {};
    return patients.filter((p) => {
      const s = p?._schema || schema || "public";
      return map[`${s}::${p.id}`] === "none_in_window";
    });
  }, [patients, schedule, schema]);
  const windowText = confirmationWindowWarning(schedule?.windowDays);

  const updateRule = (index, days) => {
    setSettings((prev) => {
      const rules = [...(prev?.rules || [])];
      // Store the raw input. Coercing here would snap the field back on every
      // backspace, making it impossible to clear and retype (7 -> 3) without
      // selecting all first. saveSettings does the conversion and clamping.
      rules[index] = { ...rules[index], days_before: days };
      return { ...prev, rules };
    });
  };

  const addRule = () =>
    setSettings((prev) => ({
      ...prev,
      rules: [...(prev?.rules || []), { days_before: 1 }],
    }));

  const removeRule = (index) =>
    setSettings((prev) => ({
      ...prev,
      rules: (prev?.rules || []).filter((_, i) => i !== index),
    }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Convert days back to the stored hours contract. A rule left mid-edit
      // (an emptied field, per updateRule above) must never be posted as ""
      // or NaN, so daysToHours clamps to a sane minimum and the id is
      // regenerated from the result.
      const normalizedRules = (settings?.rules || []).map((rule) => {
        const hours = daysToHours(rule.days_before);
        return { id: `${hours}h`, hours_before: hours };
      });
      const settingsToSave = { ...settings, rules: normalizedRules };
      const response = await fetch(`${getBackendURL()}/twilio/confirmation-settings/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ schema: schema || "public", settings: settingsToSave }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      setSettings({
        ...payload.settings,
        rules: (payload.settings.rules || []).map((r) => ({
          ...r,
          days_before: hoursToDays(r.hours_before),
        })),
      });
      if (typeof payload.messaging_enabled === "boolean") {
        setMessagingEnabled(payload.messaging_enabled);
      }
      const report = payload.report || {};
      toast({
        title: "Confirmation schedule saved",
        description: report.scheduled
          ? `Scheduled ${report.scheduled} confirmation${report.scheduled === 1 ? "" : "s"}.`
          : report.cancelled
            ? `Cancelled ${report.cancelled} queued confirmation${report.cancelled === 1 ? "" : "s"}.`
            : undefined,
        status: "success",
        duration: 4000,
      });
      loadSchedule();
    } catch (error) {
      toast({
        title: "Could not save confirmation schedule",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const selectAll = () => {
    // Open AND reachable: a row with only an email is asked by email.
    const ids = patients.filter((p) => isConfirmationOpen(p) && (hasPhone(p) || hasEmail(p))).map((p) => p.id);
    onSelectAllUnconfirmed(ids);
    onClose();
  };

  const selectNoAuto = () => {
    onSelectAllUnconfirmed(noAutoPatients.map((p) => p.id));
    onClose();
  };

  // "Apply replies from message history": replies that never went through the
  // reply handler (misrouted, imported, or answered by AI chat) leave the
  // appointments they answer at U — or at whatever staff set by hand. This
  // previews (dry run) and then applies the latest C/R reply per appointment
  // in the chosen date range, across every provider schema on the page — see
  // confirmations.reconcile_confirmation_replies. After Apply the dialog stays
  // open as the record of what changed.
  const reconcileSchemas = useMemo(() => {
    const tagged = patients.map((p) => p?._schema).filter(Boolean);
    return [...new Set([...(providerSchemas || []), ...tagged, schema || "public"].filter(Boolean))];
  }, [providerSchemas, patients, schema]);
  const closedReconcile = { open: false, loading: false, error: null, report: null, applying: false, done: false };
  const [reconcileRange, setReconcileRange] = useState({ start: "", end: "" });
  const [reconcile, setReconcile] = useState(closedReconcile);
  useEffect(() => {
    if (isOpen) setReconcileRange({ start: date || "", end: date || "" });
  }, [isOpen, date]);

  const runReconcile = (dryRun) => reconcileConfirmationReplies({
    schemas: reconcileSchemas, startDate: reconcileRange.start, endDate: reconcileRange.end, dryRun, userEmail,
  });

  const previewReconcile = async () => {
    setReconcile({ ...closedReconcile, open: true, loading: true });
    try {
      const report = await runReconcile(true);
      setReconcile({ ...closedReconcile, open: true, report });
    } catch (error) {
      setReconcile({ ...closedReconcile, open: true, error: error.message });
    }
  };

  const applyReconcile = async () => {
    setReconcile((prev) => ({ ...prev, applying: true }));
    try {
      const report = await runReconcile(false);
      setReconcile({ ...closedReconcile, open: true, done: true, report });
      if (onStatusesChanged) await onStatusesChanged();
      loadSchedule();
    } catch (error) {
      setReconcile((prev) => ({ ...prev, applying: false, error: error.message }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Appointment confirmations</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {messagingEnabled === false && (
              <Alert status="warning" borderRadius="md" data-testid="sms-off-warning">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">SMS is turned off</AlertTitle>
                  <AlertDescription fontSize="sm">{smsOffWarning(schema)}</AlertDescription>
                </Box>
              </Alert>
            )}
            <Text fontSize="sm" color="gray.600">
              {tally.confirmed} confirmed · {tally.reschedule} reschedule ·{" "}
              {tally.awaiting} awaiting
            </Text>

            <Button colorScheme="teal" onClick={selectAll}>
              Select all unconfirmed patients
            </Button>
            <Text fontSize="xs" color="gray.500">
              Selects every patient on this date who has not replied C or R and
              has a phone number. Send from the toolbar button once selected.
            </Text>

            {rescheduleRequests.length > 0 && (
              <>
                <Divider />
                <Text fontWeight="semibold">
                  Reschedule requests ({rescheduleRequests.length})
                </Text>
                <Text fontSize="xs" color="gray.500">
                  These patients replied R. They are never texted again
                  automatically — rebook them, then set their status back to U.
                </Text>
                <Box as="ul" data-testid="reschedule-queue" pl={4}>
                  {rescheduleRequests.map((p) => (
                    <Text as="li" key={p.id} fontSize="sm">
                      {p.patient_name || p.name || "Unknown"}
                      {hasPhone(p) && <PatientPhones row={p} compact inline prefix=" — " />}
                      {p.date_ ? ` — ${p.date_}` : ""}
                    </Text>
                  ))}
                </Box>
              </>
            )}

            <Divider />

            <Text fontWeight="semibold">Scheduled confirmations</Text>
            {!settings ? (
              <Text fontSize="sm" color="gray.500">Loading…</Text>
            ) : (
              <VStack align="stretch" spacing={3}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb={0} fontSize="sm">Send automatically</FormLabel>
                  <Switch
                    isChecked={Boolean(settings.enabled)}
                    onChange={(e) =>
                      setSettings((prev) => ({ ...prev, enabled: e.target.checked }))
                    }
                  />
                </FormControl>

                {(settings.rules || []).map((rule, index) => (
                  <HStack key={index}>
                    <Input
                      size="sm"
                      type="number"
                      min={0.5}
                      step={0.5}
                      width="100px"
                      value={rule.days_before ?? ""}
                      aria-label={`Rule ${index + 1} days before`}
                      onChange={(e) => updateRule(index, e.target.value)}
                    />
                    <Text fontSize="sm">days before the appointment</Text>
                    <IconButton
                      size="xs"
                      aria-label={`Remove rule ${index + 1}`}
                      onClick={() => removeRule(index)}
                      icon={<span aria-hidden>×</span>}
                    />
                  </HStack>
                ))}
                <Button size="xs" variant="outline" onClick={addRule} alignSelf="flex-start">
                  Add reminder
                </Button>

                <Text fontSize="xs" color="gray.500">
                  Each reminder goes out at the same time of day as the
                  appointment itself — a 3 PM visit with a 3-day reminder texts
                  at 3 PM, three days before, in the clinic's timezone.
                </Text>
                <Box>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    isLoading={saving}
                    onClick={saveSettings}
                  >
                    Save schedule
                  </Button>
                </Box>
              </VStack>
            )}
            <Divider />
            <Text fontWeight="semibold">Apply replies from message history</Text>
            <Text fontSize="xs" color="gray.500">
              For appointments in this range, set the status from the patient's latest C or R
              text in the SMS log — for replies that were logged without being processed.
              You'll see exactly what would change before anything is written.
            </Text>
            <HStack align="flex-end" spacing={3} flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>From</Text>
                <Input
                  size="sm"
                  type="date"
                  width="170px"
                  value={reconcileRange.start}
                  aria-label="Apply replies from date"
                  onChange={(e) => setReconcileRange((r) => ({ ...r, start: e.target.value }))}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>To</Text>
                <Input
                  size="sm"
                  type="date"
                  width="170px"
                  value={reconcileRange.end}
                  aria-label="Apply replies to date"
                  onChange={(e) => setReconcileRange((r) => ({ ...r, end: e.target.value }))}
                />
              </Box>
              <Button
                size="sm"
                colorScheme="purple"
                variant="outline"
                isDisabled={!reconcileRange.start || !reconcileRange.end || reconcileRange.end < reconcileRange.start}
                onClick={previewReconcile}
                data-testid="reconcile-replies"
              >
                Process Confirmations
              </Button>
            </HStack>
            <ProcessConfirmationsDialog
              isOpen={reconcile.open}
              onClose={() => setReconcile(closedReconcile)}
              loading={reconcile.loading}
              error={reconcile.error}
              view={reconcile.report ? buildReconcileView(reconcile.report, { done: reconcile.done }) : null}
              isApplying={reconcile.applying}
              onApply={applyReconcile}
            />
            {noAutoPatients.length > 0 && (
              <>
                <Divider />
                <Text fontWeight="semibold">
                  Won't receive a scheduled confirmation (within {schedule?.windowDays ?? "?"} days) ({noAutoPatients.length})
                </Text>
                <Text fontSize="xs" color="gray.500">{windowText}</Text>
                <Box as="ul" data-testid="no-auto-group" pl={4}>
                  {noAutoPatients.map((p) => (
                    <Text as="li" key={p.id} fontSize="sm">
                      {p.patient_name || p.name || "Unknown"}
                      {hasPhone(p) && <PatientPhones row={p} compact inline prefix=" — " />}
                    </Text>
                  ))}
                </Box>
                <Box>
                  <Button size="sm" colorScheme="orange" variant="outline" onClick={selectNoAuto}>
                    Select these
                  </Button>
                </Box>
              </>
            )}

          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AppointmentConfirmationModal;
